import { decodeWaveform } from "@/lib/audio-waveform";
import { useCallback, useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { resolveAudioUrl } from "./data/api";

type Props = {
  /** s3:// URI (presigned on demand), or a direct http/blob URL. */
  uri: string;
  /** Small caption shown left of the waveform (e.g. "Clean", "5 dB"). */
  tag?: string;
  compact?: boolean;
};

const BAR_COUNT = 72;
const PLACEHOLDER = Float32Array.from({ length: BAR_COUNT }, () => 0.15);

export function AudioPlayer({ uri, tag, compact }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const rafRef = useRef<number>(0);
  const playAttempt = useRef(0);
  const pendingPlay = useRef(false);
  const currentUri = useRef(uri);
  currentUri.current = uri;
  const [retry, setRetry] = useState(0);
  const [url, setUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [waveform, setWaveform] = useState<Float32Array>(PLACEHOLDER);
  const [waveformError, setWaveformError] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setWaveform(PLACEHOLDER);
    setProgress(0);
    setError(null);
    setIsPlaying(false);
    pendingPlay.current = false;
    playAttempt.current++;
    setWaveformError(false);
    resolveAudioUrl(uri)
      .then((u) => !cancelled && setUrl(u))
      .catch(() => !cancelled && setError("Audio could not be loaded."));
    return () => {
      cancelled = true;
    };
  }, [uri, retry]);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    decodeWaveform(url, BAR_COUNT)
      .then((bars) => { if (!cancelled) setWaveform(bars); })
      .catch(() => { if (!cancelled) setWaveformError(true); });
    return () => { cancelled = true; };
  }, [url]);

  const tick = useCallback(() => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    setProgress(a.currentTime / a.duration);
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  async function toggle() {
    const a = audioRef.current;
    if (!a || !url) return;
    if (pendingPlay.current || !a.paused) {
      playAttempt.current++;
      pendingPlay.current = false;
      a.pause();
      setIsPlaying(false);
      cancelAnimationFrame(rafRef.current);
      return;
    }
    const attempt = ++playAttempt.current;
    pendingPlay.current = true;
    setIsPlaying(true);
    setError(null);
    try {
      if (a.error) a.load();
      await a.play();
    } catch (cause) {
      if (attempt !== playAttempt.current || currentUri.current !== uri || audioRef.current !== a) return;
      setIsPlaying(false);
      if (!((cause instanceof Error || cause instanceof DOMException) && cause.name === "AbortError")) {
        setError("Audio could not be played. Press play to retry.");
      }
    } finally {
      if (attempt === playAttempt.current) pendingPlay.current = false;
    }
  }

  function seek(e: React.MouseEvent<HTMLDivElement>) {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    a.currentTime = ratio * a.duration;
    setProgress(ratio);
  }

  return (
    <div className={`stt-player ${compact ? "stt-player--compact" : ""}`}>
      <button
        className="stt-play-btn"
        type="button"
        disabled={!url}
        aria-label={isPlaying ? "Pause" : "Play"}
        onClick={() => void toggle()}
      >
        {isPlaying ? <Pause size={14} /> : <Play size={14} style={{ marginLeft: 1 }} />}
      </button>
      {waveformError && <span className="text-xs text-muted-foreground">Waveform unavailable</span>}
      {tag ? <span className="stt-player-tag">{tag}</span> : null}
      <div className="stt-waveform-wrap" onClick={seek} role="presentation">
        <div className="stt-wbars">
          {Array.from(waveform).map((amp, i) => (
            <div
              key={i}
              className={`stt-wbar ${i / BAR_COUNT < progress ? "stt-wbar--played" : "stt-wbar--unplayed"}`}
              style={{ height: `${Math.max(8, amp * 100)}%` }}
            />
          ))}
        </div>
        <div className="stt-playhead" style={{ left: `${progress * 100}%` }} />
      </div>
      <audio
        key={uri}
        ref={audioRef}
        src={url ?? undefined}
        onError={(event) => { if (event.currentTarget === audioRef.current && currentUri.current === uri) setError("Audio could not be played. Press play to retry."); }}
        onPlay={(event) => {
          if (event.currentTarget !== audioRef.current || currentUri.current !== uri) return;
          setIsPlaying(true);
          rafRef.current = requestAnimationFrame(tick);
        }}
        onPause={(event) => {
          if (event.currentTarget !== audioRef.current || currentUri.current !== uri) return;
          playAttempt.current++;
          pendingPlay.current = false;
          setIsPlaying(false);
          cancelAnimationFrame(rafRef.current);
        }}
        onEnded={(event) => {
          if (event.currentTarget !== audioRef.current || currentUri.current !== uri) return;
          setIsPlaying(false);
          setProgress(0);
          cancelAnimationFrame(rafRef.current);
        }}
        className="sr-only"
      />
      {error && <span role="alert" className="stt-player-err">{error}{!url && <button type="button" onClick={() => setRetry((value) => value + 1)}>Retry audio</button>}</span>}
    </div>
  );
}
